/* eslint-disable lingui/no-unlocalized-strings */

import { Sparkle } from "@phosphor-icons/react";
import { ResumeDto } from "@reactive-resume/dto";
import { cn } from "@reactive-resume/utils";

import { COACH_INTAKE_COLORS } from "@/client/pages/coach/optimize/coach-theme";

/**
 * Was any AI suggestion accepted on this résumé?
 *
 * Reads `metadata.aiOptimizedAt`, stamped on the first accept by `useCoachSuggestionActions`. Kept
 * next to the markers so the two layouts and any future consumer cannot disagree about what
 * "optimized" means.
 */
export const isAiOptimized = (resume: ResumeDto) =>
  Boolean(resume.data?.metadata?.aiOptimizedAt);

/**
 * Corner ribbon across the top-right of a résumé card.
 *
 * ⚠️ GEOMETRY IS LOAD-BEARING, so the four numbers below are not arbitrary. A 92px square clips the
 * corner; the band is 132px wide — longer than the 96px the corner diagonal needs — so both its ends
 * are cut off by that square and it reads as a band passing THROUGH the corner rather than a shape
 * floating near it. `top: 22px` / `right: -30px` then place the band's centre so it meets the top edge
 * and the right edge the same distance (68px) from the corner. Change one and the ribbon stops being
 * symmetric about the diagonal, which is immediately visible.
 *
 * `rotate-45` is clockwise, which is the direction that cuts the TOP-RIGHT corner: the band descends
 * left to right, from a point on the top edge to a point on the right edge. Counter-clockwise would
 * describe a top-LEFT corner and would leave the band hanging off this one.
 *
 * ⚠️ `pointer-events-none` — the whole card is a click target that opens the builder, and a ribbon
 * that swallowed clicks would leave a dead patch in its corner. The cost is that it cannot carry a
 * `title` tooltip, which is why the band spells out the words instead of showing a bare icon.
 *
 * YELLOW IS THE OPTIMIZATION FLOW'S OWN ACCENT, from COACH_INTAKE_COLORS — the same value on the
 * intake checklist and the scan card. Deliberately not a theme token: this names one feature, so it
 * should look like that feature regardless of the theme the student is running. Black text rather than
 * white, because nothing legible sits on #EDB55A at 9px in white.
 */
export const AiOptimizedRibbon = ({ className }: { className?: string }) => (
  <div
    className={cn(
      "pointer-events-none absolute right-0 top-0 z-20 size-[92px] overflow-hidden",
      className,
    )}
  >
    <div
      className="absolute right-[-30px] top-[22px] w-[132px] rotate-45 py-1 text-center text-[9px] font-bold uppercase leading-none tracking-wider shadow-[0_2px_8px_rgba(0,0,0,0.45)]"
      style={{ backgroundColor: COACH_INTAKE_COLORS.accent, color: "#1A1205" }}
    >
      AI Optimized
    </div>
  </div>
);

/**
 * The same fact as a pill, for the list layout.
 *
 * A corner ribbon needs a corner. The list renders each résumé as a table-like row, so there is no
 * corner to cut and the marker has to sit inline with the title instead. Same accent, so the two
 * layouts read as the same badge in two shapes rather than two different states.
 */
export const AiOptimizedBadge = ({ className }: { className?: string }) => (
  <span
    title="AI suggestions were applied to this resume"
    className={cn(
      "inline-flex h-5 shrink-0 items-center gap-x-1 rounded-full border px-2 text-[10px] font-medium leading-none",
      className,
    )}
    style={{
      color: COACH_INTAKE_COLORS.accent,
      borderColor: `${COACH_INTAKE_COLORS.accent}55`,
      backgroundColor: `${COACH_INTAKE_COLORS.accent}1f`,
    }}
  >
    <Sparkle size={11} weight="fill" />
    AI Optimized
  </span>
);
